import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { App as AntApp, Button, Input, Modal, Switch } from 'antd';
import AppBar from '../components/AppBar';
import DocViewer from '../components/DocViewer';
import FileViewerLazy from '../components/FileViewerLazy';
import { OptionSheet } from '../components/SheetSelect';
import { AsyncButton, Chip, I, Skeletons } from '../components/kit';
import * as formsApi from '../api/bookingForms';
import { CODE_TITLE, isAuthCode, rentalSstText } from '../lib/bookingFormTypes';
import { SIDE_LABEL, sideOfKey } from '../lib/bookingFormParties';
import { listedCount } from '../lib/inventoryList';
import { signedDocsFor } from '../lib/agreementDocs';
import { REOPEN_POINTS, convertBlockedByRevision, revisePoints, revisionRows } from '../lib/formRevisions';


// FULL-SCREEN · /forms/:id — booking-form detail + e-signature, in three tabs.
//
//   Overview · what this deal is, what it's linked to, where the signing stands
//   Document · every field of the form, grouped as the create screen groups them
//   Signing  · the signatory list and every signing action
//
// Loads getBookingFormsById(id) → { form_ref, form_type, status_raw, form_data,
// formatted, signatories[], linked_listing, linked_sale, generated_listing, … }.
// Every mutation reloads the form. On-device signing uses a self-contained canvas
// pad (mouse + touch).

// ── value formatting ────────────────────────────────────────────────────────
// The page prints what the DOCUMENT says, never the raw stored value: `1250000`
// reads as RM 1,250,000 and `vacant` as "Vacant on full payment". Ringgit figures
// derived from a percentage or a month count come from the server's `formatted`
// block — the very same numbers the Handlebars template prints — so the screen can
// never quote a figure the signed letter disagrees with.

const blank = (v) => v === undefined || v === null || v === '';
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

const txt = (v) => String(v);
const money = (v) => {
  const n = num(v);
  return n === null ? String(v) : `RM ${n.toLocaleString('en-MY', { maximumFractionDigits: 2 })}`;
};
const rm = (formattedAmount) => (formattedAmount ? `RM ${formattedAmount}` : null);
/** "3% · RM 37,500.00" — the percentage as agreed, and what it comes to. */
const pctAmt = (key) => (v, fmt) => [`${num(v) ?? v}%`, rm(fmt?.[key])].filter(Boolean).join(' · ');
/** "1 month · RM 3,500.00" — months of rent, and what they come to. */
const moAmt = (key) => (v, fmt) => [plural(v, 'month'), rm(fmt?.[key])].filter(Boolean).join(' · ');

function plural(v, unit) {
  const n = num(v);
  if (n === null) return String(v);
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}
const months = (v) => plural(v, 'month');
const years = (v) => plural(v, 'year');
const days = (v) => plural(v, 'day');
const pct = (v) => `${num(v) ?? v}%`;
const pctPa = (v) => `${num(v) ?? v}% p.a.`;
const date = (v) => (dayjs(v).isValid() ? dayjs(v).format('DD/MM/YYYY') : String(v));
const yesNo = (v) => (v === true || v === 'true' ? 'Yes' : 'No');
const from = (map) => (v) => map[v] || String(v);

const VACANT_TYPE = { vacant: 'Vacant on full payment', existing_tenancy: 'Subject to existing tenancy' };
const TERMINATION = { no_termination: 'No termination during tenancy', terminable_after: 'Terminable after period, with notice' };
const CAPACITY = {
  'registered proprietor': 'Registered proprietor',
  'duly authorised representative': 'Authorised representative (POA)',
  trustee: 'Trustee',
};
// One registry of [label, formatter] per form_data key, so the Document tab and the
// Overview's key-terms list can never label or format the same field differently.
const FIELD = {
  date: ['Date', date],
  property_address: ['Address', txt],

  selling_price_rm: ['Selling price', money],
  // Two keys, one fact — and the same shape for every money term below: it is
  // quoted either as a share of the price / of the rent, or as the flat sum
  // agreed instead, and `<term>_basis` decides which of them the form actually
  // carries. Only the populated one renders — `row()` drops a key the form does
  // not hold — so listing both needs no branch anywhere below.
  earnest_deposit_percent: ['Earnest deposit', pctAmt('earnest_deposit')],
  balance_deposit_percent: ['Balance deposit', pctAmt('balance_deposit')],
  balance_purchase_price_percent: ['Balance purchase', pctAmt('balance')],
  balance_deposit_rm: ['Balance deposit', money],
  balance_purchase_price_rm: ['Balance purchase', money],
  balance_month: ['Balance payable within', months],
  grace_period: ['Grace period', months],
  interest_percent: ['Interest on late payment', pctPa],

  rental_rm: ['Monthly rental', money],
  tax_percent: ['SST', pct],
  // ALR/EL: "Exclusive of SST" or "Inclusive of 8% SST" — see rentalSstText.
  rental_sst: ['Rental SST', (v, fmt, fd) => rentalSstText(fd)],
  advance_rental_month: ['Advance rental', moAmt('advance_rental')],
  security_deposit_month: ['Security deposit', moAmt('security_deposit')],
  utility_deposit_month: ['Utility deposit', moAmt('utility_deposit')],
  earnest_deposit_month: ['Earnest deposit', moAmt('earnest_deposit')],
  advance_rental_rm: ['Advance rental', money],
  security_deposit_rm: ['Security deposit', money],
  utility_deposit_rm: ['Utility deposit', money],
  earnest_deposit_rm: ['Earnest deposit', money],
  stamping_fee: ['Stamping fee', money],

  tenancy_period: ['Tenancy period', years],
  renew_year: ['Option to renew', years],
  tenancy_from: ['Tenancy from', date],
  tenancy_to: ['Tenancy to', date],

  vacant_possession_type: ['Vacant possession', from(VACANT_TYPE)],
  vacant_possession: ['Vacant possession', yesNo],
  working_day: ['SPA signing deadline', days],
  fittings_furnishings: ['Fittings & furnishings', txt],
  furnish: ['Furnishings', txt],
  business_nature: ['Nature of business', txt],
  termination_clause_type: ['Termination clause', from(TERMINATION)],
  termination_after_year: ['Terminable after', years],
  termination_notice_month: ['Notice period', months],
  ta_sign_date: ['TA signing deadline', date],
  payment_type: ['Payment method', txt],
  payment_ref_no: ['Payment reference', txt],
  stakeholder: ['Stakeholder', txt],
  legal_matters: ['Legal matters', txt],
  other_terms: ['Other conditions', txt],

  professional_fee_type: ['Professional fee', txt],
  // The exclusive letters print their fee as figures rather than the general
  // letter's one free-text line. Clause 8's stakeholding earnest is separate on
  // purpose: on CID's printed pad it differs from the earnest in the schedule.
  professional_fee_percent: ['Professional fee', (v) => `${v}% incl. SST`],
  professional_fee_month: ['Professional fee', (v) => `${plural(v, 'month')} of rental`],
  stakeholder_earnest_percent: ['Stakeholding earnest', pct],
  sst_percent: ['SST on fee', pct],
  owner_address: ['Vendor address', txt],
  owner_sst_id: ['Vendor SST ID', txt],
  landlord_address: ['Landlord address', txt],
  owner_capacity: ['Signing capacity', from(CAPACITY)],
  poa_date: ['POA date', txt],
  poa_court: ['High Court', txt],
  poa_reg_no: ['POA registration no', txt],
  trustee_for: ['Trustee for', txt],
  appointment_basis: ['Appointment basis', txt],
  // Period and its unit are two stored keys but one fact — read them as one row
  // ("6 months") rather than a bare number sitting above a bare "Months".
  appointment_period: ['Appointment period', (v, _fmt, fd) => plural(v, fd?.period_type === 'days' ? 'day' : 'month')],
  // Same stored key, read under the heading its own document uses. The ES/EL
  // sections below name this alias instead; `row()` resolves the label from here
  // and the value from `appointment_period`.
  exclusive_period: ['Exclusive period', (v, _fmt, fd) => plural(v, fd?.period_type === 'days' ? 'day' : 'month')],
  commencing_from: ['Commencing from', date],
  commencing_to: ['Commencing to', date],
  remark: ['Remark', txt],
};

// Document tab — the same section names the create screen uses, so a form reads in
// the order it was filled in.
const SECTIONS = {
  OTP: [
    ['Property', ['date', 'property_address']],
    ['Purchase price & deposits', ['selling_price_rm', 'earnest_deposit_percent', 'earnest_deposit_rm', 'balance_deposit_percent', 'balance_deposit_rm', 'balance_purchase_price_percent', 'balance_purchase_price_rm', 'balance_month', 'grace_period', 'interest_percent']],
    ['Terms', ['vacant_possession_type', 'working_day', 'fittings_furnishings', 'payment_type', 'payment_ref_no', 'stakeholder', 'legal_matters', 'other_terms']],
  ],
  OTR: [
    ['Property', ['date', 'property_address']],
    ['Rental & deposits', ['rental_rm', 'advance_rental_month', 'advance_rental_rm', 'security_deposit_month', 'security_deposit_rm', 'utility_deposit_month', 'utility_deposit_rm', 'earnest_deposit_month', 'earnest_deposit_rm', 'stamping_fee']],
    ['Tenancy period', ['tenancy_period', 'renew_year', 'tenancy_from', 'tenancy_to']],
    ['Terms', ['furnish', 'business_nature', 'termination_clause_type', 'termination_after_year', 'termination_notice_month', 'ta_sign_date', 'payment_type', 'payment_ref_no', 'stakeholder', 'other_terms']],
  ],
  ALS: [
    ['Property', ['date', 'property_address']],
    ['Price & payment schedule', ['selling_price_rm', 'earnest_deposit_percent', 'earnest_deposit_rm', 'balance_deposit_percent', 'balance_deposit_rm', 'balance_purchase_price_percent', 'balance_purchase_price_rm', 'balance_month', 'grace_period', 'interest_percent']],
    ['Fee & authorisation', ['professional_fee_type', 'vacant_possession', 'owner_capacity', 'poa_date', 'poa_court', 'poa_reg_no', 'trustee_for']],
    ['Appointment', ['appointment_basis', 'appointment_period', 'commencing_from', 'commencing_to', 'remark']],
  ],
  ALR: [
    ['Property', ['date', 'property_address']],
    ['Rental & deposits', ['rental_rm', 'rental_sst', 'advance_rental_month', 'advance_rental_rm', 'security_deposit_month', 'security_deposit_rm', 'utility_deposit_month', 'utility_deposit_rm', 'earnest_deposit_month', 'earnest_deposit_rm', 'professional_fee_type']],
    ['Appointment', ['appointment_basis', 'appointment_period', 'commencing_from', 'commencing_to', 'furnish', 'owner_capacity', 'poa_date', 'poa_court', 'poa_reg_no', 'trustee_for', 'remark']],
  ],
  // The exclusive letters: no appointment BASIS (exclusive is the basis), and a
  // fee broken into the figures its printed pad quotes.
  ES: [
    ['Property', ['date', 'property_address']],
    ['Price & payment schedule', ['selling_price_rm', 'earnest_deposit_percent', 'earnest_deposit_rm', 'balance_deposit_percent', 'balance_deposit_rm', 'balance_purchase_price_percent', 'balance_purchase_price_rm', 'balance_month', 'grace_period', 'interest_percent']],
    ['Fee & authorisation', ['professional_fee_percent', 'stakeholder_earnest_percent', 'sst_percent', 'vacant_possession', 'furnish', 'owner_capacity', 'poa_date', 'poa_court', 'poa_reg_no', 'trustee_for']],
    ['Exclusive period', ['exclusive_period', 'commencing_from', 'commencing_to', 'owner_address', 'owner_sst_id', 'remark']],
  ],
  EL: [
    ['Property', ['date', 'property_address']],
    ['Rental & deposits', ['rental_rm', 'rental_sst', 'advance_rental_month', 'advance_rental_rm', 'security_deposit_month', 'security_deposit_rm', 'utility_deposit_month', 'utility_deposit_rm', 'earnest_deposit_month', 'earnest_deposit_rm', 'professional_fee_month', 'sst_percent']],
    ['Exclusive period', ['exclusive_period', 'commencing_from', 'commencing_to', 'furnish', 'owner_capacity', 'poa_date', 'poa_court', 'poa_reg_no', 'trustee_for', 'landlord_address', 'remark']],
  ],
};

// Overview — the handful of terms worth seeing without opening the document.
const KEY_TERMS = {
  // Both halves of each money-term pair, so the row survives whichever basis the
  // form was written on — `row()` drops the key it does not carry.
  OTP: ['earnest_deposit_percent', 'earnest_deposit_rm', 'balance_purchase_price_percent', 'balance_purchase_price_rm', 'vacant_possession_type', 'stakeholder'],
  OTR: ['tenancy_period', 'advance_rental_month', 'advance_rental_rm', 'security_deposit_month', 'security_deposit_rm', 'furnish', 'stakeholder'],
  ALS: ['professional_fee_type', 'appointment_basis', 'appointment_period', 'owner_capacity'],
  ALR: ['professional_fee_type', 'appointment_basis', 'appointment_period', 'furnish'],
  ES: ['professional_fee_percent', 'stakeholder_earnest_percent', 'exclusive_period', 'owner_capacity'],
  EL: ['professional_fee_month', 'sst_percent', 'exclusive_period', 'furnish'],
};

const IS_RENT = (c) => c === 'OTR' || c === 'ALR' || c === 'EL';

// Module tabs — same scrollable chip row as the sale detail page, so a form and a
// sale are navigated the same way. Scrolling (rather than three squeezed segments)
// is also what lets the Signing chip carry its count without truncating.
const SEGS = ['overview', 'document', 'signing'];
const SEG_LABEL = { overview: 'Overview', document: 'Document', signing: 'Signing' };

/**
 * Row keys that read a DIFFERENT form_data key than their own name.
 *
 * The exclusive letters store their period in `appointment_period` — the same
 * field, so the ALS→listing→OTP prefill chain keeps working — but print it under
 * "Exclusive Period". Rather than a second stored key that would have to be kept
 * in step, the section lists an alias and this resolves it back.
 */
// rental_sst reads off the rental: an older letter has no rental_sst_basis but
// still states its SST, and an exclusive one carries no tax_percent.
const SOURCE_KEY = { exclusive_period: 'appointment_period', rental_sst: 'rental_rm' };

/** Render one key as a label/value row, or nothing when the form doesn't carry it. */
function row(key, fd, fmt) {
  const spec = FIELD[key];
  const src = SOURCE_KEY[key] || key;
  if (!spec || blank(fd[src])) return null;
  const [label, format] = spec;
  return { key, label, value: format(fd[src], fmt, fd) };
}

export default function FormDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { message } = AntApp.useApp();

  const [form, setForm] = useState(null);
  // Tab lives in the URL, mirroring SaleDetailPage. That makes /forms/:id?tab=signing
  // linkable — which the guided tour needs so Back can return to a signing step,
  // and which is useful on its own for pointing someone at the signatures.
  // `replace` so the tab row doesn't stack history entries.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedTab = searchParams.get('tab');
  const tab = SEGS.includes(wantedTab) ? wantedTab : 'overview';
  const setTab = (t) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (t === 'overview') next.delete('tab'); else next.set('tab', t);
    return next;
  }, { replace: true });
  const [signSig, setSignSig] = useState(null); // signatory currently signing on-device
  // A blank party's own name and NRIC, typed at the pad (see `padParty` below).
  const [padName, setPadName] = useState('');
  const [padNric, setPadNric] = useState('');
  const [issueOpen, setIssueOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [docHtml, setDocHtml] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const load = () =>
    formsApi
      .getBookingFormsById(id)
      .then((r) => setForm(r?.data ?? r))
      .catch((e) => message.error(e.message));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const statusRaw = form?.status_raw;
  const code = form?.form_type;
  const fd = form?.form_data || {};
  const fmt = form?.formatted || {};
  const signatories = form?.signatories || [];
  const signedCount = signatories.filter((s) => s.status === 'SIGNED').length;

  const canSign = ['DRAFT', 'PENDING_SIGNING'].includes(statusRaw);
  const canRevokeSig = !['APPROVED', 'REJECTED', 'CANCELLED'].includes(statusRaw);
  // Once a sale has been raised off this form the form is no longer the agent's to
  // kill — cancelling it would cut a live sale loose from the document that justifies
  // it. The server has always refused this; the button did not, so the agent typed a
  // reason and only then hit the wall. Matches the admin portal, which hides it too.
  const linkedToSale = !!form?.linked_sale || !!form?.sale_id;
  const canCancel = !linkedToSale && !['CANCELLED', 'REJECTED'].includes(statusRaw);
  // Still the agent's own draft, so they can put it into force. Signing does not gate
  // this — a deal agreed on paper is issued straight from DRAFT. The server sends
  // `issuable`; the status list is the fallback for a cached older payload.
  const canIssue = form?.issuable ?? ['DRAFT', 'PENDING_SIGNING', 'SIGNED'].includes(statusRaw);
  // Changing it once it has left DRAFT: out for signature → Edit (back to a draft,
  // signatures cleared); issued → Revise (a copy that replaces it when issued).
  const canReopen = !!form?.reopenable;
  const canRevise = !!form?.revisable;

  // The headline figure: what the deal is worth. Rent forms quote it per month.
  const headline = IS_RENT(code) ? fd.rental_rm : fd.selling_price_rm;

  /* The side label when the signatory at the pad is a PARTY of an agreement whose
     name is still blank — the agent left that half for the customer to fill in, and
     signing on the agent's phone skips the customer's own fill-in page. The name is
     taken here instead, or the signature lands under an empty line; the server
     refuses it without one. Witnesses are filled in on their row before the pad is
     offered, so they never reach this. */
  const padSide = signSig ? sideOfKey(signSig.key) : null;
  const padParty = signSig && form?.fill_sign_sides?.length && padSide && !signSig.is_witness && !signSig.name
    ? (form.fill_sign_sides.find((x) => x.side === padSide)?.label || SIDE_LABEL[padSide] || 'party')
    : null;
  const openPad = (sig) => { setPadName(''); setPadNric(''); setSignSig(sig); };

  // Capture an on-device signature for the active signatory, then reload.
  const doSign = async (dataUrl) => {
    await formsApi.createBookingFormsOnDeviceSignById(id, {
      signatory_key: signSig.key,
      signature_data_url: dataUrl,
      ...(padParty
        ? { party_name: padName.trim(), ...(padNric.trim() ? { party_nric: padNric.trim() } : {}) }
        : {}),
    });
    message.success('Signature captured');
    setSignSig(null);
    await load();
  };

  // Back to a draft under the same number. Everything signed so far was assent to the
  // old terms, so the server cancels it all — the confirm says so before it happens.
  const reopenForm = async () => {
    await formsApi.createBookingFormsReopenById(id);
    message.success('Back to a draft — make your changes, then send it for signing again');
    setReopenOpen(false);
    nav(`/forms/${id}/edit`);
  };

  // An issued form is never changed in place: the revision is a new draft copy, and the
  // server hands back the one already open rather than starting a second.
  const reviseForm = async () => {
    const r = await formsApi.createBookingFormsReviseById(id);
    const rev = r?.data ?? r;
    setReviseOpen(false);
    nav(`/forms/${rev.id}/edit`);
  };
  const openRevision = form?.open_revision;
  const startRevise = () => (openRevision ? nav(`/forms/${openRevision.id}`) : setReviseOpen(true));

  // The header's one action: a draft opens the editor; out for signature → Edit
  // (confirm, then back to a draft); issued → Revise, or straight to the revision
  // already open on it.
  const changeLabel = statusRaw === 'DRAFT' || canReopen ? 'Edit' : canRevise ? (openRevision ? 'Revision' : 'Revise') : null;
  const changeForm = () => (statusRaw === 'DRAFT' ? nav(`/forms/${id}/edit`) : canReopen ? setReopenOpen(true) : startRevise());

  // Issuing is the agent's own call — nothing is submitted to anyone — but it is not a
  // small one: it puts the document into force, locks the linked listing under offer,
  // and the only way back out is cancelling the whole form. Hence the confirm step.
  const issueForm = async () => {
    await formsApi.createBookingFormsIssueById(id);
    message.success('Form issued');
    setIssueOpen(false);
    await load();
  };

  // A reason is mandatory — the same rule HQ's cancel endpoint enforces. It becomes
  // the permanent record of why a document parties may already have signed was killed.
  const cancelForm = async () => {
    const reason = cancelReason.trim();
    if (!reason) { message.warning('A cancellation reason is required'); return; }
    await formsApi.createBookingFormsCancelById(id, { reason });
    message.success('Form cancelled');
    setCancelOpen(false);
    nav('/forms');
  };

  // Open the server-rendered document (Handlebars → HTML, incl. captured signatures +
  // signed dates) in the in-app viewer. NOT window.open — mobile browsers block popups
  // opened after an await, which made this button silently do nothing on phones.
  // Print / PDF: a paper copy for signing carries the attached LOU's number. The server
  // gives it first — only once the agreement is complete enough to sign — and the viewer
  // then prints the fresh copy. Until then it prints as it is, and says why.
  const numberLouForPrint = async () => {
    if (!form?.lou_included || form?.lou_ref) return null;
    const r = await formsApi.createBookingFormsNumberLou(id);
    const d = r?.data ?? r;
    if (!d?.numbered) {
      if (d?.missing?.length) {
        message.warning(`The Letter of Undertaking prints without its number until the agreement is complete — still needed: ${d.missing.slice(0, 3).join(', ')}${d.missing.length > 3 ? '…' : ''}`);
      }
      return null;
    }
    const p = await formsApi.listBookingFormsPreview(id);
    load();
    return (p?.data ?? p)?.html || null;
  };

  const viewDocument = async () => {
    const r = await formsApi.listBookingFormsPreview(id);
    const d = r?.data ?? r;
    if (!d?.html) throw new Error('Could not render the document');
    setDocHtml(d.html);
  };

  // Issued OTP/OTR → the sale it becomes, opened on the normal create screen with
  // everything the form already knows filled in: parties, property, price, listing.
  //
  // This used to POST straight to convert-to-sale and drop the agent into the EDIT
  // screen of a bare draft — which meant re-entering the purchaser and vendor they had
  // just typed into the form. Now the create screen is the create screen, prefilled;
  // nothing is written until they save. Mirrors the admin detail page's Convert to Sale.
  const convertToSale = () =>
    nav('/sales/new', {
      state: {
        fromBookingForm: {
          id: form.id,
          form_ref: form.form_ref,
          sale_type: form.sale_type,
          listing_id: form.listing_id || null,
          form_data: form.form_data || {},
        },
      },
    });

  // Issued ALS/ALR → the listing it authorises, prefilled from the letter and
  // locked to it (mirrors the admin detail page's "Convert to Listing").
  const convertToListing = () =>
    nav('/listings/new', {
      state: {
        fromAuthForm: {
          id: form.id,
          form_ref: form.form_ref,
          sale_type: form.sale_type,
          branch_id: form.branch_id,
          form_data: form.form_data || {},
        },
      },
    });

  const keyTerms = (KEY_TERMS[code] || []).map((k) => row(k, fd, fmt)).filter(Boolean);
  // The documents signed together with this agreement.
  const attached = [
    fd.include_inventory_list === true && { k: 'Inventory List', v: `${listedCount(fd.inventory)} item${listedCount(fd.inventory) === 1 ? '' : 's'}` },
    form?.lou_included && {
      k: `${IS_RENT(code) ? 'Landlord' : 'Vendor'}'s Letter of Undertaking`,
      v: form.lou_ref || 'Numbered when sent for signing',
    },
  ].filter(Boolean);
  const linked = [
    form?.linked_listing && {
      k: 'src', icon: '⌂', ref: form.linked_listing.listing_ref,
      sub: form.linked_listing.property_address || 'Source listing',
      tone: form.linked_listing.status === 'ACTIVE' ? 'ok' : 'warn',
      chip: String(form.linked_listing.status || '').replace(/_/g, ' '),
      to: `/listings/${form.linked_listing.id}`,
    },
    form?.generated_listing && {
      k: 'gen', icon: '⌂', ref: form.generated_listing.listing_ref,
      sub: 'Listing created from this letter',
      tone: form.generated_listing.status === 'ACTIVE' ? 'ok' : undefined,
      chip: String(form.generated_listing.status || '').replace(/_/g, ' '),
      to: `/listings/${form.generated_listing.id}`,
    },
    form?.linked_sale && {
      k: 'sale', icon: '$', ref: form.linked_sale.sale_ref,
      sub: 'Sale created from this form',
      chip: form.linked_sale.status,
      to: `/sales/${form.linked_sale.id}`,
    },
    ...revisionRows(form),
  ].filter(Boolean);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* The ref gets the whole first line; the form type and its status share the
          second, so neither is squeezed by the one action the bar carries. Changing
          the form lives here only — a labelled pill (Edit / Revise), big enough to
          find, rather than a second full-width button down the page. */}
      <AppBar
        back
        title={<span style={{ fontFamily: 'ui-monospace,monospace', letterSpacing: '-.01em' }}>{form?.form_ref || 'Form'}</span>}
        sub={form ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 4, minWidth: 0 }}>
            <Chip label={form.status} style={{ fontSize: 11, padding: '2px 8px' }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{CODE_TITLE[code] || code}</span>
          </span>
        ) : ''}
        right={
          form && changeLabel ? (
            <button
              type="button"
              data-tour="form-change"
              onClick={changeForm}
              style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 13px 0 11px', borderRadius: 999, border: '1px solid color-mix(in srgb, var(--primary) 35%, transparent)', background: 'var(--chip)', color: 'var(--primary)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}
            >
              <I.pen width={15} height={15} />
              {changeLabel}
            </button>
          ) : null
        }
      />

      {!form ? (
        <div style={{ padding: 18 }}><Skeletons n={3} height={70} /></div>
      ) : (
        <>
          <div className="scrx" style={{ display: 'flex', gap: 8, padding: '14px 15px', overflowX: 'auto', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
            {SEGS.map((s) => (
              <div
                data-tour={`form-tab-${s}`}
                key={s}
                onClick={() => setTab(s)}
                style={{ flex: 'none', whiteSpace: 'nowrap', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, cursor: 'pointer', background: tab === s ? 'var(--primary)' : 'var(--surface2)', color: tab === s ? '#fff' : 'var(--muted)', border: `1px solid ${tab === s ? 'var(--primary)' : 'var(--border)'}` }}
              >
                {SEG_LABEL[s]}
                {s === 'signing' && signatories.length > 0 && ` · ${signedCount}/${signatories.length}`}
              </div>
            ))}
          </div>

          {/* ═══ OVERVIEW ═══════════════════════════════════════════════════ */}
          {tab === 'overview' && (
            <div className="scrx" style={pane}>
              {/* First thing on the page once signing is done — where the form stands
                  outranks what the deal is worth when the agent is waiting on someone. */}
              {canIssue && <ReadyToIssue statusRaw={statusRaw} hasListing={!!form.listing_id} missing={form.missing_to_issue} onFinish={statusRaw === 'DRAFT' ? () => nav(`/forms/${id}/edit`) : null} onIssue={() => setIssueOpen(true)} />}

              <div style={{ ...card, gap: 4 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--primary)' }}>
                  {CODE_TITLE[code] || code}
                </div>
                {!blank(headline) && (
                  <div style={{ fontSize: 27, fontWeight: 700, letterSpacing: '-.028em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
                    {money(headline)}
                    {IS_RENT(code) && <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted)' }}> / month</span>}
                  </div>
                )}
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                  {fd.property_address || form.property || '—'}
                </div>
              </div>

              {attached.length > 0 && (
                <>
                  <div style={sectionLabel}>Signed with this agreement</div>
                  <div data-demo="attached-docs" style={{ ...card, padding: '2px 13px' }}>
                    {attached.map((r, i) => <KV key={r.k} k={r.k} v={r.v} last={i === attached.length - 1} />)}
                  </div>
                </>
              )}

              {/* The listing moved on beneath a form that's still live — the agent
                  needs to hear it here, not when HQ fails to approve it. */}
              {form.listing_warning && (
                <div style={{ background: 'color-mix(in srgb, var(--warn) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--warn) 35%, transparent)', borderRadius: 12, padding: '14px 15px', fontSize: 12.5, color: 'var(--warn)', lineHeight: 1.5 }}>
                  {form.listing_warning}
                </div>
              )}

              {linked.length > 0 && (
                <>
                  <div style={sectionLabel}>Linked</div>
                  <div style={{ ...card, padding: 0, overflow: 'hidden', gap: 0 }}>
                    {linked.map((l, i) => (
                      <div
                        key={l.k}
                        onClick={() => nav(l.to)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 15px', cursor: 'pointer', borderBottom: i < linked.length - 1 ? '1px solid var(--border)' : 'none' }}
                      >
                        <span style={{ flex: 'none', width: 28, height: 28, borderRadius: 9, background: 'var(--chip)', color: 'var(--primary)', display: 'grid', placeItems: 'center', fontSize: 13 }}>{l.icon}</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>{l.ref}</span>
                          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.sub}</span>
                        </span>
                        {l.chip && <Chip label={l.chip} tone={l.tone} />}
                        <span style={{ flex: 'none', color: 'var(--muted)' }}>›</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* What this form becomes. Both open a prefilled create screen rather
                  than writing anything, so they are plain navigation. */}
              {form.convertible && !convertBlockedByRevision(form) && (
                <Button data-tour="form-convert-sale" type="primary" block onClick={convertToSale} style={{ height: 44, borderRadius: 12 }}>
                  Convert to sale
                </Button>
              )}
              {/* Say why it is gone while a revision is open, rather than leaving a gap. */}
              {convertBlockedByRevision(form) && (
                <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, textAlign: 'center', padding: '0 8px' }}>
                  {convertBlockedByRevision(form)}
                </div>
              )}
              {form.listing_convertible && (
                <Button data-tour="form-convert-listing" type="primary" block onClick={convertToListing} style={{ height: 44, borderRadius: 12 }}>
                  Convert to listing
                </Button>
              )}

              {keyTerms.length > 0 && (
                <>
                  <div style={sectionLabel}>Key terms</div>
                  <div data-tour="form-key-terms" style={{ ...card, padding: '2px 13px' }}>
                    {keyTerms.map((r, i) => <KV key={r.key} k={r.label} v={r.value} last={i === keyTerms.length - 1} />)}
                  </div>
                </>
              )}

              {/* Signing at a glance — tapping moves to the tab that can act on it */}
              <div onClick={() => setTab('signing')} style={{ ...card, cursor: 'pointer', gap: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{signedCount} of {signatories.length} signed</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {signedCount === signatories.length && signatories.length > 0
                        ? 'All parties have signed'
                        : nextUp(signatories)}
                    </div>
                  </div>
                  <span style={{ flex: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: 13 }}>›</span>
                </div>
                <Track done={signedCount} total={signatories.length} />
              </div>

              <div style={{ height: 6 }} />
            </div>
          )}

          {/* ═══ DOCUMENT ═══════════════════════════════════════════════════ */}
          {tab === 'document' && (
            <div className="scrx" style={pane}>
              {(SECTIONS[code] || []).map(([title, keys]) => {
                const rows = keys.map((k) => row(k, fd, fmt)).filter(Boolean);
                if (!rows.length) return null;
                return (
                  <Fragment key={title}>
                    <div style={sectionLabel}>{title}</div>
                    <div style={{ ...card, padding: '2px 13px' }}>
                      {rows.map((r, i) => <KV key={r.key} k={r.label} v={r.value} last={i === rows.length - 1} />)}
                    </div>
                  </Fragment>
                );
              })}

              {/* The buttons that used to end this tab now live in the action bar at
                  the bottom of the page, where they are reachable from all three. */}
              <div style={{ height: 6 }} />
            </div>
          )}

          {/* ═══ SIGNING ════════════════════════════════════════════════════ */}
          {tab === 'signing' && (
            <div className="scrx" style={pane}>
              {canIssue && <ReadyToIssue statusRaw={statusRaw} hasListing={!!form.listing_id} missing={form.missing_to_issue} onFinish={statusRaw === 'DRAFT' ? () => nav(`/forms/${id}/edit`) : null} onIssue={() => setIssueOpen(true)} />}

              <div data-tour="form-signing-progress" style={{ ...card, gap: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{signedCount} of {signatories.length} signed</span>
                  <Chip label={form.status} />
                </div>
                <Track done={signedCount} total={signatories.length} />
              </div>

              {/* Inviting a SIDE comes before tracking signatures — on a form whose
                  parties are still blank, the list below has nobody to chase. */}
              {form.fill_sign_sides && canSign && (
                <FillSign form={form} formId={id} onForm={setForm} onReload={load} />
              )}

              <div style={sectionLabel}>Signatories</div>
              <div style={{ ...card, padding: 0, overflow: 'hidden', gap: 0 }}>
                {signatories.length === 0 ? (
                  <div style={{ padding: 18, fontSize: 13, color: 'var(--muted)' }}>No signatories on this form.</div>
                ) : (
                  signatories.map((sig, i) => (
                    <div key={sig.key} style={{ borderBottom: i < signatories.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <SignatoryRow
                        sig={sig}
                        formId={id}
                        canSign={canSign}
                        canRevokeSig={canRevokeSig}
                        onReload={load}
                        onOpenPad={openPad}
                      />
                    </div>
                  ))
                )}
              </div>

              {canCancel && (
                <button
                  onClick={() => setCancelOpen(true)}
                  style={{ height: 44, border: '1px solid color-mix(in srgb,var(--bad) 35%,var(--border))', borderRadius: 12, background: 'transparent', color: 'var(--bad)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                >
                  Cancel form · deal fell through
                </button>
              )}
              {/* Say why it is gone, rather than leaving a blank where the button was. */}
              {linkedToSale && !['CANCELLED', 'REJECTED'].includes(statusRaw) && (
                <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, textAlign: 'center', padding: '0 8px' }}>
                  This form has become a sale, so it can no longer be cancelled.
                  Ask HQ to void {form?.linked_sale?.sale_ref ? `${form.linked_sale.sale_ref} ` : 'the sale '}first.
                </div>
              )}
              <div style={{ height: 6 }} />
            </div>
          )}

          {/* Reading the document is the point of the whole record, so it is a fixed
              action bar rather than the last thing on one of three tabs — agents were
              switching to Document and scrolling past every field to find it, and
              reported not finding it at all. */}
          <div data-tour="form-doc-bar" style={actionBar}>
            {form.pdf_url && (
              <Button onClick={() => setPdfOpen(true)} style={{ flex: 'none', paddingInline: 16, height: 40 }}>Signed PDF</Button>
            )}
            <AsyncButton type="primary" block onClick={viewDocument} onError={(e) => message.error(e.message)}>
              View document · {form.form_ref}
            </AsyncButton>
          </div>
        </>
      )}

      {/* Issuing puts the document into force and is only undone by cancelling the whole
          form, so this lists what changes rather than asking "are you sure?". */}
      <Modal open={issueOpen} title="Issue this booking form?" footer={null} onCancel={() => setIssueOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            {form?.form_ref} takes effect from now. Nobody needs to approve it.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
            {form?.listing_id && <li>The linked listing is locked <strong style={{ color: 'var(--text)' }}>under offer</strong>, so no one else can raise a form on it.</li>}
            {isAuthCode(code)
              ? <li>You can then use it to back a listing.</li>
              : <li>You can then convert it into a sale.</li>}
            {form?.revises?.status_raw === 'APPROVED' && (
              <li><strong style={{ color: 'var(--text)' }}>{form.revises.form_ref}</strong> is cancelled as revised by this form{form?.listing_id ? ', and the listing passes to this one' : ''}.</li>
            )}
            <li>Signatures can no longer be revoked. To change it later you revise it — a copy with its own number that replaces it once issued.</li>
          </ul>
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>To undo it you would have to cancel the form.</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
            <Button block onClick={() => setIssueOpen(false)}>Not yet</Button>
            <AsyncButton data-tour="form-issue-confirm" type="primary" block onClick={issueForm} onError={(e) => message.error(e.message)}>Issue form</AsyncButton>
          </div>
        </div>
      </Modal>

      {/* Cancel with a reason */}
      <Modal open={cancelOpen} title="Cancel this booking form?" footer={null} onCancel={() => setCancelOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>This marks the form as cancelled. A reason is required for the record.</div>
          <Input.TextArea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} maxLength={1000} placeholder="Reason (e.g. deal fell through)" />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button block onClick={() => setCancelOpen(false)}>Keep form</Button>
            <AsyncButton block danger disabled={!cancelReason.trim()} onClick={cancelForm} onError={(e) => message.error(e.message)}>Cancel form</AsyncButton>
          </div>
        </div>
      </Modal>

      {/* Edit after signing started: say what is lost before it is lost. */}
      <Modal open={reopenOpen} title="Edit this form?" footer={null} onCancel={() => setReopenOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
            {REOPEN_POINTS.map((t) => <li key={t}>{t}</li>)}
          </ul>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button block onClick={() => setReopenOpen(false)}>Not now</Button>
            <AsyncButton data-tour="form-reopen-confirm" type="primary" block onClick={reopenForm} onError={(e) => message.error(e.message)}>Edit form</AsyncButton>
          </div>
        </div>
      </Modal>

      {/* Revise an issued form */}
      <Modal open={reviseOpen} title="Revise this form?" footer={null} onCancel={() => setReviseOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
            {revisePoints(form).map((t) => <li key={t}>{t}</li>)}
          </ul>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button block onClick={() => setReviseOpen(false)}>Not now</Button>
            <AsyncButton data-tour="form-revise-confirm" type="primary" block onClick={reviseForm} onError={(e) => message.error(e.message)}>Start revision</AsyncButton>
          </div>
        </div>
      </Modal>

      {/* On-device signature pad */}
      <Modal
        open={!!signSig}
        title={signSig ? `Sign — ${signSig.label || signSig.role}` : ''}
        footer={null}
        onCancel={() => setSignSig(null)}
        destroyOnHidden
        centered
        width={360}
      >
        {signSig && padParty && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
                <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>{padParty}&rsquo;s full name (as per IC)
              </label>
              <Input value={padName} onChange={(e) => setPadName(e.target.value)} placeholder="Full name as on NRIC / passport" autoComplete="off" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
                {padParty}&rsquo;s NRIC / passport no. <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <Input value={padNric} onChange={(e) => setPadNric(e.target.value)} placeholder="e.g. 901231-14-5678" autoComplete="off" />
            </div>
          </div>
        )}
        {signSig && signedDocsFor(form, signSig.key).length > 1 && (
          <div data-demo="pad-signs" style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 10 }}>
            One signature — placed on the {signedDocsFor(form, signSig.key).join(', ')}.
          </div>
        )}
        {signSig && (
          <SignaturePad
            label={signSig.name
              ? `${signSig.name}${signSig.nric ? ` · ${signSig.nric}` : ''}`
              : (padParty && padName.trim()) || signSig.label || signSig.role}
            blocked={!!padParty && !padName.trim()}
            onConfirm={doSign}
          />
        )}
      </Modal>

      {/* In-app rendered document (popup-safe on mobile) */}
      {docHtml && <DocViewer title={form?.form_ref || 'Document'} html={docHtml} onClose={() => setDocHtml(null)} beforePrint={numberLouForPrint} />}

      {/* In-app stored-PDF viewer */}
      {pdfOpen && form?.pdf_url && (
        <FileViewerLazy
          title={form.form_ref || 'Document'}
          fileName={`${form.form_ref || 'form'}.pdf`}
          url={form.pdf_url}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  );
}

/** Who the form is waiting on — the line under "n of m signed". */
function nextUp(signatories) {
  const pending = signatories.find((s) => s.status === 'PENDING');
  if (pending) return `${pending.name || pending.label} — link sent, awaiting signature`;
  const todo = signatories.find((s) => s.status !== 'SIGNED');
  if (todo) return `${todo.name || todo.label} — not started`;
  return signatories.length ? 'All parties have signed' : 'No signatories yet';
}

function Track({ done, total }) {
  const pctDone = total ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ height: 5, borderRadius: 3, background: 'var(--surface2)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pctDone}%`, borderRadius: 3, background: 'var(--primary)', transition: 'width .35s ease' }} />
    </div>
  );
}

/**
 * The form is still a draft, and issuing it is the agent's own move.
 *
 * This replaced a card that told the agent to sit tight while HQ reviewed a
 * fully-signed form. There is no reviewer now, so the one thing that actually matters —
 * putting the form into force, which locks the listing and unlocks the sale — is a
 * button rather than a wait. It leads the overview because a draft that nobody has
 * issued does nothing at all, and that is easy to miss once the signatures are in.
 */
function ReadyToIssue({ statusRaw, hasListing, missing, onFinish, onIssue }) {
  const allSigned = statusRaw === 'SIGNED';
  // A draft saves half-finished; the server lists what is left before it can be
  // issued (and refuses the issue while anything is), so say so here rather than
  // offer a button that can only fail.
  const left = Array.isArray(missing) ? missing : [];
  if (left.length) {
    return (
      <div data-tour="form-ready-to-issue" style={{ background: 'color-mix(in srgb, var(--warn, #d48806) 9%, transparent)', border: '1px solid color-mix(in srgb, var(--warn, #d48806) 35%, transparent)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>
            Not finished — {left.length} {left.length === 1 ? 'item' : 'items'} still needed
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            {left.join(' · ')}
          </div>
        </div>
        {onFinish && (
          <Button type="primary" block onClick={onFinish} style={{ height: 42, borderRadius: 11 }}>
            Finish the form
          </Button>
        )}
      </div>
    );
  }
  return (
    <div data-tour="form-ready-to-issue" style={{ background: 'color-mix(in srgb, var(--primary) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--primary) 30%, transparent)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ display: 'flex', gap: 11 }}>
        <span style={{ flex: 'none', width: 20, height: 20, borderRadius: 50, background: allSigned ? 'var(--ok)' : 'var(--primary)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11.5, fontWeight: 800, marginTop: 1 }}>
          {allSigned ? '✓' : '!'}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>
            {allSigned ? 'All parties have signed' : 'Not yet issued'}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            {hasListing
              ? 'Issue it to put the form into force and lock the listing under offer. Nobody needs to approve it.'
              : 'Issue it to put the form into force. Nobody needs to approve it — until you do, the form does nothing.'}
          </div>
        </div>
      </div>
      {/* Opens the confirm — the request itself is fired from the modal. */}
      <Button data-tour="form-issue-btn" type="primary" block onClick={onIssue} style={{ height: 42, borderRadius: 11 }}>
        Issue form
      </Button>
    </div>
  );
}

/**
 * ── Sequential fill & sign — the two SIDES of an agreement, invited in turn ──
 *
 * WHY THIS IS NOT JUST ANOTHER BUTTON ON THE SIGNATORY LIST BELOW
 * "Send link" down there asks one NAMED signatory to confirm a name the agent
 * already typed. This asks a SIDE OF THE DEAL — a party who is not on the document
 * yet — to put their own name on it and sign, with the other half redacted out of
 * what they read. Leon asked for it (2026-09-04) because a buyer will not put their
 * name on an offer the owner can read before accepting it, and an owner will not put
 * theirs on an offer that has not been made: requiring both up front was stopping
 * these agreements being raised at all. Different question, different unit — merging
 * the two rows would hide which of them actually NAMES the party.
 *
 * `fill_sign_sides` is the availability test: the server sends null on an
 * authorisation letter (one party, nothing to sequence). The sides, their labels and
 * their signatory keys are read off that payload rather than derived here, because
 * this screen's `form_type` is the SHORT CODE ('OTP') while lib/bookingFormParties
 * keys on the stored type ('AGREEMENT_LETTER') — deriving them locally would quietly
 * return null on every form.
 */
function FillSign({ form, formId, onForm, onReload }) {
  const sides = form.fill_sign_sides || [];
  const rows = sides.map((s) => ({
    ...s,
    sig: (form.signatories || []).find((x) => x.key === s.signatory_key) || {},
  }));
  const chain = chainLine(form.signing_flow, sides);
  // What each side's one signature goes onto.
  const signsFor = (side) => signedDocsFor(form, `${side}_1`);
  /* The hand-over currently ARMED for a side, or ''. Only one can be, and only
     until it fires — `sent_at` means the other side's link is already out, so there
     is nothing left to arm or cancel.

     The sheet SEEDS its forward box from this, and that seeding is what makes the
     explicit `forward_email: fwd || null` below safe: the box always shows the
     arrangement as it stands, so leaving it alone re-states it and emptying it is a
     deliberate cancel. An empty box that did not show what was already armed would
     turn every re-send — even one that only fixes a typo in the address — into a
     silent cancellation of the hand-over. */
  const armedForwardTo = (side) => {
    const f = form.signing_flow?.forward;
    // A skipped hand-over (`skipped_at`) is history too: it never fires, and seeding
    // it would re-arm one the server refuses once that side has signed.
    return f && !f.sent_at && !f.skipped_at && f.side === side ? f.email : '';
  };

  return (
    <>
      <div style={sectionLabel}>Fill &amp; sign</div>
      {/* Said once, while there is nothing to say instead: an agent who has never
          used this has no reason to expect a blank party to be legitimate. */}
      {!chain && (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: -4 }}>
          Each side can fill in their own name and sign in turn — you don&rsquo;t need both parties up front.
        </div>
      )}
      <div style={{ ...card, padding: 0, overflow: 'hidden', gap: 0 }}>
        {rows.map((r, i) => (
          <FillSignRow
            key={r.side}
            row={r}
            /* Exactly two sides on a two-sided document, so "the other one" is a
               find rather than an index — the order the server lists them in is
               the signing order, not a fixed purchaser-then-owner pair. */
            other={rows.find((x) => x.side !== r.side) || {}}
            armedForward={armedForwardTo((rows.find((x) => x.side !== r.side) || {}).side)}
            keepBothHidden={!!form.signing_flow?.keep_both_hidden}
            formId={formId}
            onForm={onForm}
            onReload={onReload}
            signs={signsFor(r.side)}
            last={i === rows.length - 1}
          />
        ))}
      </div>
      {chain && (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: -4 }}>{chain}</div>
      )}
    </>
  );
}

/**
 * The chain as it stands, in one quiet line.
 *
 * Three facts the agent chose a moment ago — who went first, who is hidden from
 * them, where it hands over — and they are only ever read to check them. As three
 * rows they would outweigh the two rows that can still be acted on.
 */
function chainLine(flow, sides) {
  if (!flow) return null;
  const first = sides.find((s) => s.side === flow.first_side);
  const second = sides.find((s) => s.side !== flow.first_side);
  const parts = [`${first?.label || 'First side'} invited first`];
  /* Keeping both sides apart outranks the first link's own choice: "<second> hidden
     from them" beside it would suggest the first side is visible to the second,
     which in this mode it never is. */
  if (flow.keep_both_hidden) parts.push('both sides are kept hidden from each other');
  else if (flow.hide_counterparty && second) parts.push(`${second.label} hidden from them`);
  /* `forward.side` is the side the link goes TO; the side whose signature sets it
     off is the other one. Named from the forward itself, not from `first_side`: a
     hand-over armed from the second side's row is triggered by the second side, and
     "invited first · forwards when they sign" read as though the first side did it.
     Any other key the server puts on `forward` is ignored. */
  const fwd = flow.forward;
  if (fwd?.email) {
    const target = sides.find((s) => s.side === fwd.side)?.label || 'other side';
    const trigger = sides.find((s) => s.side !== fwd.side)?.label || 'other side';
    parts.push(fwd.skipped_at
      // Stamped by the server when the hand-over could not go — the target had signed
      // or already held a live link. It never fires later, so it is reported, not
      // described as pending.
      ? `not forwarded — the ${target} ${fwd.skip_reason === 'LINK_ALREADY_OUT' ? 'already had a link' : 'had already signed'}`
      : fwd.sent_at
        ? `the ${target}'s link was forwarded to ${fwd.email} on ${dayjs(fwd.sent_at).format('D MMM YYYY')}`
        : `when the ${trigger} signs, the ${target}'s link goes to ${fwd.email}`);
  }
  return parts.join(' · ');
}

/** One side of the deal: who is on the document for it, and the invite that gets them there. */
function FillSignRow({ row, other, armedForward, keepBothHidden, formId, onForm, onReload, signs, last }) {
  const { message } = AntApp.useApp();
  const sig = row.sig || {};
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(sig.email || '');
  const [hide, setHide] = useState(true);
  // The agreement-wide "keep both sides hidden" setting. Seeded from the stored flow
  // each time the sheet opens, and only sent when the agent moves it — the server
  // reads an absent key as "leave it", so a plain resend never rewrites it.
  const [keepBoth, setKeepBoth] = useState(false);
  const [forward, setForward] = useState('');
  const emailRef = useRef(null);

  // Keep the known address prefilled when the row re-renders after a send.
  useEffect(() => { setEmail((e) => e || sig.email || ''); }, [sig.email]);

  // Focus a beat after the sheet has risen, so the keyboard doesn't shove the panel
  // around mid-animation — same timing the option pickers use.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => emailRef.current?.focus(), 260);
    return () => clearTimeout(t);
  }, [open]);

  const signed = sig.status === 'SIGNED';
  const pending = sig.status === 'PENDING';
  // Nothing left to hide once the other side has signed — and the second signer is
  // MEANT to see who is offering and the signature already on it. The server forces
  // hide_counterparty off in that case, so the toggle says so rather than promising
  // something the document will not do.
  const otherSigned = other?.sig?.status === 'SIGNED';

  // A hand-over at a side that has already signed would send them a fresh link, and a
  // fresh link replaces the signature on file — so none is offered or seeded, and the
  // server refuses one too.
  const openSheet = () => { setHide(!otherSigned); setKeepBoth(!!keepBothHidden); setForward(otherSigned ? '' : (armedForward || '')); setOpen(true); };

  const send = async () => {
    const to = email.trim();
    if (!to || !to.includes('@')) { message.warning('Enter a valid email'); return; }
    const fwd = otherSigned ? '' : forward.trim();
    if (fwd && !fwd.includes('@')) {
      message.warning(`Enter a valid email for the ${other.label || 'other side'}, or leave it blank`);
      return;
    }
    const r = await formsApi.createBookingFormsSendFillSignLinkById(formId, {
      side: row.side,
      email: to,
      // With both sides kept apart this link hides the other side regardless — the
      // server forces it too — so the body says what will actually happen.
      hide_counterparty: keepBoth || hide,
      ...(keepBoth !== !!keepBothHidden ? { keep_both_hidden: keepBoth } : {}),
      // An emptied box must send an explicit null, not nothing. The server reads an
      // ABSENT forward_email as "leave the hand-over as it is" — deliberately, so a
      // re-send that only corrects the recipient keeps a hand-over already arranged.
      // Omitting the key here therefore made CLEARING it impossible. Once the other
      // side has signed there is no box and nothing to arrange, so the key IS left
      // out: the server keeps what it holds rather than being told to clear it.
      ...(otherSigned ? {} : { forward_email: fwd || null }),
    });
    const d = r?.data ?? r;
    message.success(`Fill & sign link sent to ${to}`);
    setOpen(false);
    // The reply carries the very payload GET /:id returns, so the screen updates
    // from it rather than paying for a second round trip on a phone network.
    if (d?.form) onForm(d.form); else await onReload();
  };

  return (
    <div style={{ padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 11, borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>
            {row.label}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sig.name || (
              // Muted, not a warning: an unnamed side is the point of this flow,
              // not a mistake the agent has to go back and fix.
              <span style={{ color: 'var(--muted)', fontWeight: 500, fontStyle: 'italic' }}>Waiting for them to fill this in</span>
            )}
          </div>
          {sig.sent_to && (
            <div style={{ fontSize: 11.5, color: pending ? 'var(--warn)' : 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {signed ? 'Signed via ' : 'Link sent to '}<b>{sig.sent_to}</b>
            </div>
          )}
        </div>
        {signed ? <Chip tone="ok">Signed ✓</Chip> : pending ? <Chip tone="warn">Link sent</Chip> : <Chip tone="brand">Not started</Chip>}
      </div>
      {signs?.length > 1 && (
        <div data-demo={`signs-${row.side}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: -3 }}>
          <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>One signature on:</span>
          {signs.map((d) => (
            <span key={d} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--surface2)', border: '1px solid var(--border)' }}>{d}</span>
          ))}
        </div>
      )}

      {!signed && (
        <div style={actions}>
          <Button size="small" type={pending ? 'default' : 'primary'} onClick={openSheet}>
            {pending ? 'Resend' : 'Send fill & sign link'}
          </Button>
        </div>
      )}

      {/* A bottom sheet, like every other input surface on this shell: three fields
          under the row would push the second side off the screen, and the keyboard
          would then cover whichever one was just revealed. */}
      <OptionSheet
        open={open}
        title={pending ? `Resend to the ${row.label}` : `Invite the ${row.label}`}
        onClose={() => setOpen(false)}
        footer={
          <AsyncButton block type="primary" onClick={send} onError={(e) => message.error(e.message)}>
            {pending ? 'Resend link' : 'Send link'}
          </AsyncButton>
        }
      >
        <div style={{ padding: '4px 12px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 15px' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{row.label}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: 2 }}>
              {sig.name
                ? <>They confirm their details on the document and sign.</>
                : <>They type their own name and NRIC onto the document, read it, and sign.</>}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
              <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>Email address
            </label>
            <Input
              ref={emailRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="their@email.com"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, minHeight: 'var(--field-h)' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Keep both sides hidden from each other</span>
              <Switch checked={keepBoth} onChange={setKeepBoth} />
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
              Applies to the whole agreement — both links, including the automatic hand-over.
              Neither side ever sees the other&rsquo;s details on their copy, even after the other signs. You still see everything.
            </div>
          </div>

          {/* In both-hidden mode the per-link choice is not a choice: every link hides
              the other side, signed or not — so the switch shows on and locked, and
              that outranks the "already signed, nothing to hide" state below. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, minHeight: 'var(--field-h)' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Hide the {other.label || 'other side'}&rsquo;s details</span>
              <Switch checked={keepBoth || (hide && !otherSigned)} disabled={keepBoth || otherSigned} onChange={setHide} />
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
              {keepBoth
                ? (otherSigned
                  ? `On because both sides are kept hidden from each other — the ${other.label} has signed, but their details stay off the ${row.label}'s copy.`
                  : `On because both sides are kept hidden from each other — the ${row.label} never sees the ${other.label}'s details, even after the ${other.label} signs.`)
                : otherSigned
                  ? `The ${other.label} has already signed, so nothing can be hidden — the ${row.label} is meant to see who is on the other half of this agreement and the signature already on it.`
                  : hide
                    ? `The ${other.label}'s name, NRIC, witness and signature are taken out of the copy the ${row.label} reads, for as long as the ${other.label} has not signed. Once the ${other.label} signs, their details appear on it.`
                    : `The ${row.label} reads the whole document, including the ${other.label}'s details.`}
            </div>
          </div>

          {!otherSigned && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
              Also forward to the {other.label || 'other side'} at
            </label>
            <Input
              type="email"
              inputMode="email"
              value={forward}
              onChange={(e) => setForward(e.target.value)}
              placeholder="optional — their email"
            />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
              Their own fill &amp; sign link goes out automatically the moment the {row.label.toLowerCase()} signs.
              {armedForward
                ? ' Already arranged — empty the box to cancel it and have the form come back to you instead.'
                : ' Leave it blank and the form comes back to you to send yourself.'}
            </div>
          </div>
          )}

          {pending && sig.sent_to && (
            <div style={{ fontSize: 11.5, color: 'var(--warn)', lineHeight: 1.5 }}>
              A link is already out to <b>{sig.sent_to}</b>. Sending again cancels it — only the newest link will open the document.
            </div>
          )}

          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            They&rsquo;ll need a one-time code sent to this address before the document opens.
            The link expires in 24 hours.
          </div>
        </div>
      </OptionSheet>
    </div>
  );
}

// ── One signatory row: status chip + status-dependent actions ────────────────
function SignatoryRow({ sig, formId, canSign, canRevokeSig, onReload, onOpenPad }) {
  const { message, modal } = AntApp.useApp();
  const [sendOpen, setSendOpen] = useState(false);
  const [email, setEmail] = useState(sig.email || '');
  const [wName, setWName] = useState(sig.name || '');
  const [wNric, setWNric] = useState(sig.nric || '');
  const emailRef = useRef(null);

  // Keep the registered email prefilled when the row re-renders after a reload.
  useEffect(() => { setEmail((e) => e || sig.email || ''); }, [sig.email]);

  // Focus a beat after the sheet has risen, so the keyboard doesn't shove the panel
  // around mid-animation — same timing the option pickers use.
  useEffect(() => {
    if (!sendOpen) return undefined;
    const t = setTimeout(() => emailRef.current?.focus(), 260);
    return () => clearTimeout(t);
  }, [sendOpen]);

  const signed = sig.status === 'SIGNED';
  const pending = sig.status === 'PENDING';
  const witnessNeedsFill = sig.is_witness && (!sig.name || !sig.nric);

  const chip = signed ? (
    <Chip tone="ok">Signed ✓</Chip>
  ) : pending ? (
    <Chip tone="warn">{sig.signing_method === 'REMOTE_LINK' ? 'Link sent' : 'Awaiting'}</Chip>
  ) : (
    <Chip tone="brand">Not started</Chip>
  );

  const sendLink = async () => {
    if (!email.trim() || !email.includes('@')) { message.warning('Enter a valid email'); return; }
    await formsApi.createBookingFormsSendSigningLinkById(formId, { signatory_key: sig.key, signatory_email: email.trim() });
    message.success(`Signing link sent to ${email.trim()}`);
    setSendOpen(false); onReload();
  };
  // Both revokes are confirmed, for different reasons. Killing a link strands one the
  // signatory may be looking at right now; killing a signature throws away something a
  // real person already did and drags the whole form back to Pending signing. Wording
  // mirrors the admin's signing table.
  const revokeLink = () => {
    modal.confirm({
      title: 'Revoke this signing link?',
      content: `The link already sent to ${sig.name || 'this signatory'} stops working. You can send a new one afterwards.`,
      okText: 'Revoke link',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        try {
          await formsApi.createBookingFormsRevokeSigningLinkById(formId, { signatory_key: sig.key });
        } catch (e) { message.error(e.message); throw e; }
        message.success('Signing link revoked'); onReload();
      },
    });
  };

  const revokeSig = () => {
    modal.confirm({
      title: 'Revoke this signature?',
      content: `${sig.name || 'This signatory'} will need to sign again, and the form goes back to Pending signing.`,
      okText: 'Revoke',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        try {
          await formsApi.createBookingFormsRevokeSignatureById(formId, { signatory_key: sig.key });
        } catch (e) { message.error(e.message); throw e; }
        message.success('Signature revoked'); onReload();
      },
    });
  };
  const saveWitness = async () => {
    if (!wName.trim() || !wNric.trim()) { message.warning('Witness name & NRIC are required'); return; }
    await formsApi.patchBookingFormsWitnessById(formId, { signatory_key: sig.key, name: wName.trim(), nric: wNric.trim() });
    message.success('Witness details saved'); onReload();
  };

  // Human datetime of the e-sign (raw ISO from the server → "17 Jul 2026, 08:35").
  const at = sig.signed_at
    ? new Date(sig.signed_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : sig.at;

  return (
    <div style={{ padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sig.name || <span style={{ color: 'var(--warn)', fontStyle: 'italic' }}>Not filled in</span>}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            {sig.label || sig.role}
            {sig.nric ? ` · ${sig.nric}` : ''}
            {sig.required === false ? ' · optional' : ''}
            {signed && at ? ` · ${at}` : ''}
          </div>
          {/* where the signing link went (remote e-sign audit trail) */}
          {sig.sent_to && sig.signing_method === 'REMOTE_LINK' && (
            <div style={{ fontSize: 11.5, color: pending ? 'var(--warn)' : 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {pending ? 'Link sent to ' : 'Signed via '}<b>{sig.sent_to}</b>
            </div>
          )}
        </div>
        {chip}
      </div>

      {signed ? (
        canRevokeSig && (
          <div style={actions}>
            <Button size="small" danger onClick={revokeSig}>Revoke signature</Button>
          </div>
        )
      ) : canSign ? (
        witnessNeedsFill ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Fill in this witness before signing:</div>
            <Input size="small" value={wName} onChange={(e) => setWName(e.target.value)} placeholder="Witness full name" />
            <Input size="small" value={wNric} onChange={(e) => setWNric(e.target.value)} placeholder="Witness NRIC / REN no" />
            <div style={actions}>
              <AsyncButton size="small" type="primary" onClick={saveWitness} onError={(e) => message.error(e.message)}>Save witness</AsyncButton>
              {pending && <Button size="small" danger onClick={revokeLink}>Revoke link</Button>}
            </div>
          </div>
        ) : (
          <div style={actions}>
            <Button data-tour="form-sign-on-device" size="small" type="primary" onClick={() => onOpenPad(sig)}>Sign on device</Button>
            <Button size="small" onClick={() => setSendOpen(true)}>{pending ? 'Resend link' : 'Send link'}</Button>
            {pending && <Button size="small" danger onClick={revokeLink}>Revoke link</Button>}
          </div>
        )
      ) : null}

      {/* Send / resend the remote signing link. A bottom sheet rather than a field
          that unfolds inside the row: the row already carries three buttons and an
          audit line, and an input appearing under them pushed every signatory below
          it down the page — with the keyboard then covering the thing you just
          revealed. Here the one field it needs arrives in thumb reach, above the
          keyboard, with the recipient named so a link can't go to the wrong party. */}
      <OptionSheet
        open={sendOpen}
        title={pending ? 'Resend signing link' : 'Send signing link'}
        onClose={() => setSendOpen(false)}
        footer={
          <AsyncButton block type="primary" onClick={sendLink} onError={(e) => message.error(e.message)}>
            {pending ? 'Resend link' : 'Send link'}
          </AsyncButton>
        }
      >
        <div style={{ padding: '4px 12px 8px', display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 15px' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{sig.name || sig.label || sig.role}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {sig.label || sig.role}{sig.nric ? ` · ${sig.nric}` : ''}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
              <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>Email address
            </label>
            <Input
              ref={emailRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="signatory@email.com"
              onPressEnter={() => { sendLink().catch((e) => message.error(e.message)); }}
            />
          </div>

          {pending && sig.sent_to && (
            <div style={{ fontSize: 11.5, color: 'var(--warn)', lineHeight: 1.5 }}>
              A link is already out to <b>{sig.sent_to}</b>. Sending again cancels it — only the newest link will open the document.
            </div>
          )}

          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            They&rsquo;ll need a one-time code sent to this address before the document opens.
            The link expires in 24 hours.
          </div>
        </div>
      </OptionSheet>
    </div>
  );
}

// ── Self-contained signature pad (mouse + touch) ─────────────────────────────
// `blocked` holds the submit back while something the signature needs is missing —
// a blank party's name — so it cannot go before the field it depends on.
function SignaturePad({ label, onConfirm, blocked = false }) {
  const { message } = AntApp.useApp();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const posOf = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return undefined;
    const ctx = c.getContext('2d');
    c.width = c.offsetWidth || 300;
    c.height = 190;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Native touch listeners so preventDefault (passive:false) stops page scroll.
    const start = (e) => { e.preventDefault(); drawing.current = true; const p = posOf(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = posOf(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const end = () => { drawing.current = false; };
    c.addEventListener('touchstart', start, { passive: false });
    c.addEventListener('touchmove', move, { passive: false });
    c.addEventListener('touchend', end);
    return () => {
      c.removeEventListener('touchstart', start);
      c.removeEventListener('touchmove', move);
      c.removeEventListener('touchend', end);
    };
  }, []);

  const mDown = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = posOf(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const mMove = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const p = posOf(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
  const mUp = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const confirm = async () => {
    if (!hasDrawn) { message.warning('Please draw a signature'); return; }
    await onConfirm(canvasRef.current.toDataURL('image/png'));
  };

  return (
    // Anchored as a whole so a guided step can spotlight the pad AND its buttons
    // together — highlighting only the submit leaves the canvas under a blocker,
    // and the submit is disabled until something has been drawn on it.
    <div data-tour="form-sign-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: 190, display: 'block', border: '1.5px dashed var(--border)', borderRadius: 11, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={mDown}
        onMouseMove={mMove}
        onMouseUp={mUp}
        onMouseLeave={mUp}
      />
      <div style={{ fontSize: 11.5, color: 'var(--muted)', textAlign: 'center', marginTop: -4 }}>
        sign here{label ? ` · ${label}` : ''}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button onClick={clear} style={{ paddingInline: 16 }}>Clear</Button>
        <AsyncButton data-tour="form-sign-confirm" block type="primary" disabled={!hasDrawn || blocked} onClick={confirm} onError={(e) => message.error(e.message)}>Capture &amp; submit</AsyncButton>
      </div>
    </div>
  );
}

const pane = { flex: 1, overflowY: 'auto', padding: '16px 16px calc(20px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 12 };
// Pinned bottom action bar — same anatomy as the create screens' footer, so the
// primary move on a record sits where the primary move on a form does.
const actionBar = { padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none', display: 'flex', gap: 10 };
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column' };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: -6 };
const actions = { display: 'flex', flexWrap: 'wrap', gap: 10 };
const KV = ({ k, v, last }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '9px 0', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
    <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: 'none' }}>{k}</span>
    <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
  </div>
);
